/* eslint-disable max-lines */
import { getInitialURL } from 'expo-linking';
import { router } from 'expo-router';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import useRedirect from 'features/otp/hooks/useRedirection/useRedirection';
import { jwtDecode } from 'jwt-decode';
import React, {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { identityIdFromToken } from 'services/analytics/helpers/identityIdFromToken';
import {
  identifyUser as identifySegmentUser,
  updateRiskSessionId,
} from 'services/analytics/segment/segment';
import {
  processWebDeeplink,
  setIsLoggedIn as setDeeplinkIsLoggedIn,
} from 'services/deeplinks/deeplinkManager';
import { isABLink } from 'services/deeplinks/helpers/resolveABLink';
import { clearUserIdentityFromServices } from 'services/userInfo/userInfoManager';
import { Headers } from 'shared/constants/headers';
import { isWeb } from 'shared/constants/platform';
import { STORAGE_KEYS } from 'shared/constants/storage';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import useBiometricLogin, {
  BiometricLoginPostData,
} from 'shared/hooks/auth/useBiometricLogin';
import useCreateSession from 'shared/hooks/auth/useCreateSession';
import useIovationToken from 'shared/hooks/auth/useIovationToken';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import usePostLogin, { LoginPostData } from 'shared/hooks/auth/usePostLogin';
import { useAppLifecycle } from 'shared/hooks/useAppLifecycle/useAppLifecycle';
import { getItem, removeItem, setItem } from 'shared/utils/asyncStorage';
import {
  deleteUserRegisteredKey,
  saveHasPermanentRefreshToken,
  saveRefreshTokenForReactNative,
  saveUserIDForReactNative,
} from 'shared/utils/biometricAuth/biometricAuth';
import { logBreadcrumb, logError } from 'shared/utils/sentry/sentry';
import { clearSessionMemoryStores } from 'shared/utils/sessionMemoryStore/sessionMemoryStore';
import {
  formatPhoneNumberWithoutPrefix,
  PHONE_REGEX,
} from 'shared/utils/string/phone';

export interface HandleLogoutArguments {
  hideBiometricButton?: boolean;
  shouldNavigateToLogin?: boolean;
}

declare global {
  interface Window {
    isE2E: boolean;
  }
}

interface SignedInEventProps {
  isOriginLoginPage: boolean;
  isBiometricLogin: boolean;
  isPhoneNumberLogin: boolean;
}
interface AuthProviderProps {
  children: ReactNode;
}

export interface AuthContextProps {
  handleLogin: (
    data: LoginPostData,
    headerOverrides?: Headers,
  ) => Promise<void>;
  handleLogout: (data?: HandleLogoutArguments) => Promise<void>;
  isLoggedIn: boolean;
  handleBiometricLogin: (data: BiometricLoginPostData) => Promise<void>;
  isLoginLoading: boolean;
  showBiometricButtonAction: boolean;
  setShowBiometricButtonAction: Dispatch<SetStateAction<boolean>>;
  loginError: string | undefined;
  loginRequestStatusCode: string | undefined;
  loginPostErrorTitle: string | undefined;
  loginPostErrorMessage: string | undefined;
  loginErrorCode?: string;
  fetchIovationToken: () => Promise<string>;
  varoSessionId: string;
  getVaroSessionId: () => Promise<string>;
  clearVaroSessionId: () => Promise<void>;
  storeIdToken: (idToken: string) => Promise<void>;
  setErrorMessage: () => void;
  idToken: string;
  handleRefreshToken: (data: BiometricLoginPostData) => Promise<void>;
  tokenErrorType: string;
  loginEvaluated: boolean;
  resetAuthState: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextProps>(
  {} as AuthContextProps,
);

export function useAuth() {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error('useAuth must be used within a AuthProvider');
  }

  return value;
}

// I18nextProvider is below the AuthProvider in the app tree, this will not be a common error
export const requiredTokensErrorMessage =
  "We're having trouble connecting to Varo. If you're using a VPN or Adblocker try turning it off, then log in again.";

const AuthProvider = ({
  children,
}: AuthProviderProps): ReactElement<AuthProviderProps> => {
  const [signedInEventProps, setSignedInEventProps] =
    useState<SignedInEventProps>({
      isOriginLoginPage: false,
      isBiometricLogin: false,
      isPhoneNumberLogin: false,
    });
  const [loginEvaluated, setLoginEvaluated] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState<string>();
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [showBiometricButtonAction, setShowBiometricButtonAction] =
    useState(true);
  const [idToken, setIdToken] = useState('');
  const [loginError, setLoginError] = useState<string>();
  const [tokenErrorType, setTokenErrorType] = useState<string>('');
  const [loginRequestStatusCode, setLoginRequestStatusCode] =
    useState<string>();
  const [loginPostErrorTitle, setLoginPostTitleError] = useState<string>();
  const [loginPostErrorMessage, setLoginPostMessageError] = useState<string>();
  const [loginErrorCode, setLoginErrorCode] = useState<string>();
  const { fetchIovationToken } = useIovationToken();
  const {
    varoSessionId,
    setVaroSessionId,
    getVaroSessionId,
    clearVaroSessionId,
  } = useCreateSession();
  const { isColdStart } = useAppLifecycle();
  const isLoggedInRef = useRef<boolean>(false);
  const coldStartLogoutExecutedRef = useRef<boolean>(false);
  const { postLogin } = usePostLogin();
  const { postBiometricLogin } = useBiometricLogin();
  const { challengeRedirect } = useRedirect();

  const storeIdToken = async (idToken: string) => {
    await setItem(STORAGE_KEYS.ID_TOKEN, idToken);
    setIdToken(idToken);
    setIsLoggedIn(true);
    if (loginCredentials) {
      await saveUserIDForReactNative(loginCredentials);
    }

    // Immediately identify to Segment so all subsequent events have userId.
    // Full traits (email, phone, name, etc.) are synced later by the authenticated layout.
    const identityId = identityIdFromToken(idToken);
    await identifySegmentUser(identityId, {});
  };

  const setErrorMessage = () => {
    setLoginError(undefined);
    setLoginPostTitleError(undefined);
    setLoginPostMessageError(undefined);
  };

  const signedIn = usePressableAnalytics({
    analyticsId: 'signedIn',
    customAttributes: signedInEventProps.isOriginLoginPage
      ? {
          login_method: signedInEventProps.isBiometricLogin
            ? 'biometrics'
            : 'password',
          username_type: signedInEventProps.isPhoneNumberLogin
            ? 'phone'
            : 'email',
        }
      : undefined,
  });

  useEffect(() => {
    // this is needed to set varoSessionId on app load (for urql client)
    // specifically for cases of page refresh/app background JT-138004
    void getVaroSessionId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync login state to deeplinkManager and process web deeplink once evaluated
  useEffect(() => {
    setDeeplinkIsLoggedIn(isLoggedIn);

    if (loginEvaluated) {
      void processWebDeeplink();
    }
  }, [isLoggedIn, loginEvaluated]);

  useEffect(() => {
    if (isLoggedIn && idToken) {
      signedIn();
      setSignedInEventProps({
        isOriginLoginPage: false,
        isBiometricLogin: signedInEventProps.isBiometricLogin,
        isPhoneNumberLogin: signedInEventProps.isPhoneNumberLogin,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const showTokenError = (aToken: string) => {
    setTokenErrorType(!aToken ? 'iovationToken' : 'varoSessionId');
    setLoginPostMessageError(requiredTokensErrorMessage);
    setLoginError(requiredTokensErrorMessage);
    setIsLoginLoading(false);
  };

  const handleLogin = async (
    data: LoginPostData,
    headerOverrides?: Headers,
  ) => {
    setLoginCredentials(
      data.emailAddress === ''
        ? formatPhoneNumberWithoutPrefix(data.phoneNumber ?? '')
        : data.emailAddress,
    );

    setSignedInEventProps({
      isOriginLoginPage: true,
      isBiometricLogin: false,
      isPhoneNumberLogin: PHONE_REGEX.test(
        data.emailAddress ?? data.phoneNumber,
      ),
    });
    setIsLoginLoading(true);
    setTokenErrorType('');
    const aToken = await fetchIovationToken();
    const varoSessionId = await getVaroSessionId();

    if (!aToken || !varoSessionId) {
      showTokenError(aToken);
      return;
    }

    const baseHeaders = await getLambdaHeaders(idToken, varoSessionId);

    const headers = {
      ...baseHeaders,
      ...headerOverrides,
    };

    try {
      logBreadcrumb({
        level: 'info',
        message: 'Initiating postLogin',
      });

      const {
        idToken,
        challenge,
        session,
        refreshToken,
        errorCode,
        title,
        message,
        phoneLineNumber,
        statusCode,
        challengeId,
        identityId,
      } = await postLogin(
        {
          ...data,
          aToken,
        },
        headers,
      );
      setLoginRequestStatusCode(statusCode);

      if (challenge) {
        logBreadcrumb({
          level: 'info',
          message: 'Login challenge initiated',
        });

        setIsLoginLoading(false);

        return challengeRedirect(
          challenge,
          data.emailAddress,
          data.phoneNumber ?? '',
          phoneLineNumber ?? '',
          session ?? '',
          'login',
          OtpFlowKey.LOGIN,
          challengeId,
          identityId,
        );
      }

      if (!idToken) {
        logBreadcrumb({
          level: 'error',
          message: 'No idToken received',
        });

        setLoginPostTitleError(title);
        setLoginPostMessageError(message);
        setLoginErrorCode(errorCode);
        setLoginError('Login response missing idToken');
        setIsLoginLoading(false);
        return;
      }
      await setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      await storeIdToken(idToken);
      setLoginError('');
      setShowBiometricButtonAction(true);
      await saveRefreshTokenForReactNative(refreshToken);

      const userID =
        data.emailAddress === ''
          ? formatPhoneNumberWithoutPrefix(data.phoneNumber ?? '')
          : data.emailAddress;
      await saveUserIDForReactNative(userID);

      await saveHasPermanentRefreshToken(true);
    } catch {
      setLoginError('Login request failed');
      setLoginPostTitleError(undefined);
      setLoginPostMessageError(undefined);
      setLoginErrorCode(undefined);
      logBreadcrumb({
        level: 'error',
        message: 'Login request failed',
      });
    }
    setIsLoginLoading(false);
  };

  const handleLogout = useCallback(
    async ({
      hideBiometricButton = true,
      shouldNavigateToLogin = false,
    }: HandleLogoutArguments = {}) => {
      logBreadcrumb({
        level: 'info',
        message: 'Initiating logout',
      });

      await removeItem(STORAGE_KEYS.ID_TOKEN);

      // To prevent subsequent user getting old user data during signup (/refresh-token-trade uses refreshToken)
      // https://varomoney.atlassian.net/browse/JT-134485
      if (isWeb) {
        await removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      }

      await clearVaroSessionId();
      clearSessionMemoryStores();

      // Clear all identity services (Statsig, Segment, Sentry, etc.)
      await clearUserIdentityFromServices();

      setIdToken('');
      setIsLoggedIn(false);
      if (hideBiometricButton) {
        setShowBiometricButtonAction(false);
      }
      setLoginPostTitleError(undefined);
      setLoginPostMessageError(undefined);
      setLoginErrorCode(undefined);
      setLoginError(undefined);
      setLoginError('');
      if (shouldNavigateToLogin) {
        router.replace('/login');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoggedIn, setVaroSessionId],
  );
  const handleLogoutRef = useRef<typeof handleLogout>(handleLogout);

  const handleBiometricLogin = async (data: BiometricLoginPostData) => {
    setSignedInEventProps({
      isOriginLoginPage: true,
      isBiometricLogin: true,
      isPhoneNumberLogin: PHONE_REGEX.test(
        data.emailAddress ?? data.phoneNumber,
      ),
    });
    logBreadcrumb({
      level: 'info',
      message: 'Initiating biometric login',
    });

    setTokenErrorType('');
    const aToken = await fetchIovationToken();
    const varoSessionId = await getVaroSessionId();

    if (!aToken || !varoSessionId) {
      showTokenError(aToken);
      return;
    }

    const headers = await getLambdaHeaders(
      idToken,
      varoSessionId,
      'biometrics-login',
    );

    setIsLoginLoading(true);
    try {
      logBreadcrumb({
        level: 'info',
        message: 'Initiating postBiometricLogin',
      });

      const { idToken, refreshToken, errorCode, message, title, statusCode } =
        await postBiometricLogin(
          {
            ...data,
            aToken,
          },
          headers,
        );

      setLoginRequestStatusCode(statusCode);
      if (errorCode !== undefined) {
        logBreadcrumb({
          level: 'error',
          message: 'Biometric Login Failed' + errorCode,
        });
        setLoginError('Login failed');
        setLoginPostTitleError(title);
        setLoginPostMessageError(message);
        setLoginErrorCode(errorCode);
      }

      if (!idToken) {
        logBreadcrumb({
          level: 'error',
          message: 'No idToken received for biometric login',
        });

        setIsLoginLoading(false);
        setLoginError('Biometric response missing idToken');
        setLoginPostTitleError(title);
        setLoginPostMessageError(message);
        setLoginErrorCode(errorCode);
        return;
      }

      await storeIdToken(idToken);
      setLoginError('');
      setShowBiometricButtonAction(true);
      if (refreshToken) {
        await saveRefreshTokenForReactNative(refreshToken);
        await saveUserIDForReactNative(data.emailAddress);
        await saveHasPermanentRefreshToken(true);
      }
    } catch {
      setLoginError('Biometric request failed');
      logBreadcrumb({
        level: 'error',
        message: 'Biometric login failed',
      });
    }
    setIsLoginLoading(false);
  };

  const handleRefreshToken = async (data: BiometricLoginPostData) => {
    const aToken = await fetchIovationToken();

    const varoSessionId = await getVaroSessionId();
    const headers = await getLambdaHeaders(idToken, varoSessionId);
    await deleteUserRegisteredKey();

    setIsLoginLoading(true);
    try {
      const { idToken } = await postBiometricLogin(
        {
          ...data,
          aToken,
        },
        headers,
      );

      if (!idToken) {
        logBreadcrumb({
          level: 'error',
          message: 'No idToken received for postBiometricLogin',
        });

        setLoginError('Something went wrong');
        setIsLoginLoading(false);
        return;
      }

      await storeIdToken(idToken);
      setShowBiometricButtonAction(true);
    } catch (e) {
      logBreadcrumb({
        level: 'error',
        message: 'Error in postBiometricLogin',
      });
      logError(e);
      setLoginError('Something went wrong');
    } finally {
      setIsLoginLoading(false);
    }
  };

  useEffect(() => {
    const checkAccessToken = async (): Promise<void> => {
      try {
        const storedToken = await getItem(STORAGE_KEYS.ID_TOKEN);
        if (typeof storedToken === 'string' && storedToken.length > 0) {
          setIdToken(storedToken);
          setIsLoggedIn(true);
        }
      } catch {
        logBreadcrumb({
          level: 'error',
          message: 'Error in retrieving idToken from storage',
        });
      } finally {
        setLoginEvaluated(true);
      }
    };

    if (!loginEvaluated) {
      void checkAccessToken();
    }
  }, [loginEvaluated]);

  useEffect(() => {
    if (varoSessionId) {
      try {
        const decodedToken = jwtDecode<{ 'custom:session_id': string }>(
          varoSessionId,
        );
        updateRiskSessionId(decodedToken['custom:session_id']);
      } catch (error) {
        logError(error);
      }
    }
  }, [varoSessionId, setVaroSessionId]);

  // Keep refs in sync so the cold-start effect can read current values
  // without listing them as dependencies (which would retrigger the effect).
  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  useEffect(() => {
    handleLogoutRef.current = handleLogout;
  }, [handleLogout]);

  // Cold start: logout user when app is fully closed and reopened.
  // The executed ref prevents a second logout if the effect re-runs with the
  // same dependency values (e.g. a parent re-render that doesn't change deps).
  useEffect(() => {
    const checkColdStartLogout = async () => {
      if (
        isColdStart &&
        loginEvaluated &&
        isLoggedInRef.current &&
        !coldStartLogoutExecutedRef.current
      ) {
        coldStartLogoutExecutedRef.current = true;
        // If launched from an ablink, skip the forced /login redirect so a
        // public external-deeplink screen is not yanked away. Fall back to
        // today's behavior (force login) if the URL lookup fails.
        let isAbLinkLaunch = false;
        try {
          const initialUrl = await getInitialURL();
          isAbLinkLaunch = !!initialUrl && isABLink(initialUrl);
        } catch {
          isAbLinkLaunch = false;
        }
        logBreadcrumb({
          level: 'info',
          message: 'Cold start detected - logging out user',
        });
        void handleLogoutRef.current({
          hideBiometricButton: false,
          shouldNavigateToLogin: !isAbLinkLaunch,
        });
      }
    };
    void checkColdStartLogout();
  }, [isColdStart, loginEvaluated]);

  // used in login/createAccount screens to reset auth state (new xVaroSession is required)
  const resetAuthState = async () => {
    const storedSessionId = await getItem(STORAGE_KEYS.X_VARO_SESSION);
    if (storedSessionId) {
      // reset xVaroSession/localStorage in case user navigated to login directly
      await handleLogout({ hideBiometricButton: false });
      // fetch xVaroSession asap so it can be sent with analytics events
      await getVaroSessionId();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        isLoginLoading,
        loginEvaluated,
        showBiometricButtonAction,
        setShowBiometricButtonAction,
        handleBiometricLogin,
        handleLogin,
        handleLogout,
        loginError,
        loginRequestStatusCode,
        fetchIovationToken,
        varoSessionId,
        getVaroSessionId,
        clearVaroSessionId,
        storeIdToken,
        setErrorMessage,
        loginPostErrorTitle,
        loginPostErrorMessage,
        loginErrorCode,
        idToken,
        handleRefreshToken,
        tokenErrorType,
        resetAuthState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
